import * as THREE from 'three';
import { SketchRelationKind } from '../../../../sketch/solver/constraint/relation-compatibility';
import { SingleTargetRelation } from '../../../../sketch/solver/constraint/relationDefinition/SingleTargetRelation';
import type { SketchBezierCurveMetadata, SketchSplineObject } from '../../../../mesh/sketch/SketchSplineObject';
import { resolvePreferredEdgeLabel } from '../../model/EdgeLabelUtils';
import type { ReferenceDrivenSplineOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenSplineOnCurveConstraint';
import {cloneSketchBSplineEvaluationDefinition} from '../../../../utils/sketch/cloneSketchBSplineEvaluationDefinition';

export type { ReferenceDrivenSplineOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenSplineOnCurveConstraint';

/**
 * 参考驱动普通样条在曲线上关系定义。
 *
 * 控制点和曲线元数据共同组成不可变公式载荷，实际几何只由参数化运行时统一回写。
 */
export class ReferenceDrivenSplineOnCurveConstraint extends SingleTargetRelation<SketchSplineObject> {
	/** 引用记录稳定标识。 */
	public readonly referenceId: string;
	/** 关系面板使用的关系类型。 */
	public relationType = SketchRelationKind.OnEdge;

	/** 投影样条的世界坐标控制点。 */
	private readonly projectedControlPointWorlds: THREE.Vector3[];
	/** 用于克隆和持久化一致性的投影采样点。 */
	private readonly projectedCurvePointWorlds: THREE.Vector3[];
	/** 投影样条的参数曲线元数据。 */
	private readonly projectedCurveMetadata: SketchBezierCurveMetadata;
	/** 投影后的 Puppy CAD 内核权威 B 样条定义。 */
	private readonly projectedCurveEvaluationDefinition: SketchBSplineEvaluationDefinition | null;
	/** 关系面板显示的来源边标签。 */
	private readonly edgeLabel: string;

	/** @param options 外部普通样条投影参数。 */
	public constructor(options: ReferenceDrivenSplineOnCurveConstraintOptions) {
		super();
		this.referenceId = options.referenceId;
		this.projectedControlPointWorlds = options.projectedControlPointWorlds.map((point) => point.clone());
		this.projectedCurvePointWorlds = options.projectedCurvePointWorlds.map((point) => point.clone());
		this.projectedCurveMetadata = {
			...options.projectedCurveMetadata,
			weights: [...options.projectedCurveMetadata.weights],
		};
		// 关系寿命跨越创建、重建和 Undo/Redo，必须隔离投影结果内的可变数组。
		this.projectedCurveEvaluationDefinition = cloneSketchBSplineEvaluationDefinition(
			options.projectedCurveEvaluationDefinition,
		);
		this.edgeLabel = resolvePreferredEdgeLabel({
			runtimeSourceName: options.runtimeSourceName,
			persistedSourceDisplayLabel: options.sourceDisplayLabel,
		});
		this.name = this.edgeLabel;
	}

	/** @returns 关系面板使用的稳定显示名。 */
	public override get baseDisplayName(): string {
		return this.edgeLabel;
	}

	/** @returns 与当前投影载荷相互独立的新关系实例。 */
	public override cloneSelf(): ReferenceDrivenSplineOnCurveConstraint {
		// clone 会进入独立草图快照，精确曲线定义不得与原关系共享数组。
		return new ReferenceDrivenSplineOnCurveConstraint({
			referenceId: this.referenceId,
			projectedControlPointWorlds: this.projectedControlPointWorlds.map((point) => point.clone()),
			projectedCurvePointWorlds: this.projectedCurvePointWorlds.map((point) => point.clone()),
			projectedCurveMetadata: {
				...this.projectedCurveMetadata,
				weights: [...this.projectedCurveMetadata.weights],
			},
			...(this.projectedCurveEvaluationDefinition
				? {
					projectedCurveEvaluationDefinition:
						cloneSketchBSplineEvaluationDefinition(
							this.projectedCurveEvaluationDefinition,
						)!,
				}
				: {}),
		});
	}

	/**
	 * 创建统一参数方程使用的局部样条载荷。
	 *
	 * @returns 投影控制点和参数曲线元数据；目标尚未绑定时返回 `null`。
	 */
	public override createParametricFormulaPayload() {
		const target = this.getTargetOrThrow();
		if (!target) {
			return null;
		}

		// 关系内部保存世界极点，统一参数求解器只接收目标草图局部坐标。
		const curveEvaluationDefinition = this.projectedCurveEvaluationDefinition
			? {
				...this.projectedCurveEvaluationDefinition,
				controlPoints: this.projectedCurveEvaluationDefinition.controlPoints.map((point) => {
					const local = target.referencePlane.worldToLocal(
						new THREE.Vector3(point.x, point.y, point.z),
					);
					return {x: local.x, y: local.y, z: 0};
				}),
				weights: [...this.projectedCurveEvaluationDefinition.weights],
				knots: [...this.projectedCurveEvaluationDefinition.knots],
			}
			: null;

		return {
			kind: 'external-spline',
			referenceId: this.referenceId,
			controlPoints: this.projectedControlPointWorlds.map((point) => {
				const local = target.referencePlane.worldToLocal(point);
				return [local.x, local.y];
			}),
			curveMetadata: {
				...this.projectedCurveMetadata,
				weights: [...this.projectedCurveMetadata.weights],
			},
			...(curveEvaluationDefinition ? {curveEvaluationDefinition} : {}),
		};
	}
}
